import { Alert, Card, ImageUpload, Input, InputWrapper } from '@components';
import { FC, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { VariationTypeEnum } from '@utilities/enums';
import { 
    ErrorType, ImageType, 
    AddProductFormType, Product, 
    Variations, Category,
    UpdateProductFormType,
} from '@utilities/types';
import { ProductVariation, ProductVariationRef } from './productVariations';
import { getProductSellingPrice, getProductPurchasePrice } from '@utilities/functions';

interface ProductFormProps {
    categories: Category[];
    product?: Product;
    loading?: boolean;
    sussessMessage?: string;
    error?: ErrorType | null;
    onUpdateProduct?: (product: UpdateProductFormType) => void;
    onCreateProduct?: (product: AddProductFormType, images: File[]) => void;
}

export const ProductForm: FC<ProductFormProps> = ({ 
    categories,
    product, 
    loading = false,
    sussessMessage,
    error,
    onUpdateProduct,
    onCreateProduct,
}) => {

    const variationRef = useRef<ProductVariationRef>(null);

    const [isRemovedBackground, setIsRemovedBackground] = useState<boolean>(false);
    const [previewImages, setPreviewImages] = useState<ImageType[]>(product?.images || []);
    const [imageFiles, setImageFiles] = useState<File[]>([]);
    const [productName, setProductName] = useState<string>(product?.name || '');
    const [selectedCategory, setSelectedCategory] = useState<string>(product?._categoryID._id || '');
    const [productPrice, setProductPrice] = useState<number>(product ? getProductSellingPrice(product) : 0);
    const [productPurchasePrice, setProductPurchasePrice] = useState<number>(product ? getProductPurchasePrice(product) : 0);
    const [productDescription, setProductDescription] = useState<string>(product?.description || '');
    const [variationType, setVariationType] = useState<VariationTypeEnum>(
        product?.variationType || VariationTypeEnum.NONE,
    );
    const [variations, setVariations] = useState<Variations>(
        product?.variations || { quantity: 0 }
    );

    const categoryOptions = useMemo(()=> (
        categories?.map(category => ({
            value: category._id,
            label: category.name
        }))
    ), [categories]);

    const isFormValid = useMemo(() => (
        product ? (
            productName !== product.name ||
            selectedCategory !== product._categoryID._id ||
            productPrice !== getProductSellingPrice(product) ||
            productPurchasePrice !== getProductPurchasePrice(product) ||
            productDescription !== product.description
        ) : (
            productName !== '' &&
            selectedCategory !== '' &&
            productPrice > 0 &&
            productDescription !== '' &&
            imageFiles.length > 0 &&
            (variationType !== VariationTypeEnum.NONE ? (
                //@ts-ignore
                (variationType === VariationTypeEnum.BOTH 
                    //@ts-ignore
                    && variations?.colorSizeData.length > 0 && variations.colorSizeData[0].sizes.length > 0) ||
                //@ts-ignore
                (variationType === VariationTypeEnum.COLOR && variations?.colors.length > 0) ||
                //@ts-ignore
                (variationType === VariationTypeEnum.SIZE && variations?.sizes.length > 0)
            ) : true)
        )
    ), [
        imageFiles, product, productDescription, variationType,
        productName, productPrice, productPurchasePrice, selectedCategory, variations,
    ]);

    useEffect(()=> {
        if(sussessMessage){
            onClearForm();
        }
    }, [sussessMessage]);

    const onClearForm = useCallback(() => {
        setPreviewImages([]);
        setImageFiles([]);
        setProductName('');
        setSelectedCategory('');
        setProductPrice(0);
        setProductPurchasePrice(0);
        setProductDescription('');
        setVariationType(VariationTypeEnum.NONE);
        setVariations({ quantity: 0 });
        variationRef.current?.resetVariation();
    }, []);

    const handleButtonClick = useCallback(()=> {
        if(!isFormValid) return;

        if(product){
            const updatedProduct: UpdateProductFormType = {
                ...(selectedCategory !== product._categoryID._id && { categoryId: selectedCategory }),
                ...(productName !== product.name && { name: productName }),
                ...(productPrice !== getProductSellingPrice(product) && { price: String(productPrice) }),
                ...(productPurchasePrice !== getProductPurchasePrice(product) && { purchasePrice: String(productPurchasePrice) }),
                ...(productDescription !== product.description && { description: productDescription }),
            };

            onUpdateProduct?.(updatedProduct);
        } else {
            if(imageFiles.length > 0){
                const newProduct: AddProductFormType = {
                    name: productName,
                    categoryID: selectedCategory,
                    price: productPrice,
                    purchasePrice: productPurchasePrice > 0 ? productPurchasePrice : undefined,
                    description: productDescription,
                    variationType,
                    variations,
                    isRemovedBackground,
                };
    
                onCreateProduct?.(newProduct, imageFiles);
                onClearForm();
            }
        }
    }, [
        isRemovedBackground,
        imageFiles, isFormValid, variations,
        product, productDescription, productName, 
        productPrice, productPurchasePrice, selectedCategory, variationType, 
        onCreateProduct, onUpdateProduct, onClearForm,
    ]);

    const renderAlert = useCallback(() => {
        if(!(error || sussessMessage)) return;
        
        return (
            <div className='pt-4'>
                <Alert
                    showCloseButton
                    type={sussessMessage ? "success" : "error"}
                    message={error?.message || sussessMessage || ''}
                />
            </div>
        )
    }, [error, sussessMessage]);

    const rendrButton = useCallback(() => {
        return (
            <button
                onClick={handleButtonClick}
                className="btn btn-success mt-4 w-full"
                disabled={loading || !isFormValid}
            >
                {loading ? 'Chargement...' : product?._id ? 'Mettre à jour produit' : 'Ajouter produit'}
            </button>
        );
    }, [
        loading, product, 
        isFormValid, handleButtonClick,
    ]);

    return (
        <>
            {renderAlert()}
            <div className='grid grid-cols-1 md:grid-cols-[1fr_2fr] lg:grid-cols-[2fr_4fr] gap-6 items-start'>

                <div className="flex flex-col gap-5">
                    <Card title='Images du produit'>
                        <ImageUpload
                            images={previewImages ?? []}
                            onChange={(images, imageFiles) => {
                                setImageFiles(imageFiles);
                                setPreviewImages(images);
                            }}
                            maxImages={3}
                            baseImageUrl={process.env.REACT_APP_IMAGE_BASE_URL}
                        />
                        {/* <div className="flex items-center justify-end gap-1 mt-4">
                            <span className={`text-sm ${isRemovedBackground && 'text-primary'}`}>{
                                isRemovedBackground ? 
                                    "Suppression par IA du fond activée" : "Suppression par IA du fond désactivée"
                                }
                            </span>
                            <input
                                type="checkbox"
                                className="toggle toggle-primary"
                                checked={isRemovedBackground}
                                onChange={(e) => setIsRemovedBackground(e.target.checked)}
                            />
                        </div> */}
                    </Card>

                    <div className='hidden md:block'>
                        {rendrButton()}
                    </div>
                </div>

                <Card title='Informations Générales'>
                    <div className="grid grid-cols-1 gap-4">
                        <InputWrapper label="Nom du produit">
                            <Input
                                placeholder="Nom du produit"
                                className="input input-bordered w-full"
                                value={productName}
                                onChange={(e) => setProductName(e.target.value)}
                            />
                        </InputWrapper>

                        <InputWrapper label="Catégorie">
                            <Input
                                type="select"
                                placeholder="Sélectionner une catégorie"
                                className="input input-bordered w-full"
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                options={categoryOptions}
                            />
                        </InputWrapper>

                        <InputWrapper label="Prix de vente">
                            <Input
                                type="number"
                                min={0}
                                placeholder="Prix de vente"
                                className="input input-bordered w-full"
                                value={productPrice}
                                onChange={(e) => setProductPrice(Number(e.target.value))}
                            />
                        </InputWrapper>

                        <InputWrapper label="Prix d'achat (optionnel)">
                            <Input
                                type="number"
                                min={0}
                                placeholder="Prix d'achat"
                                className="input input-bordered w-full"
                                value={productPurchasePrice}
                                onChange={(e) => setProductPurchasePrice(Number(e.target.value))}
                            />
                        </InputWrapper>

                        <InputWrapper label="Description">
                            <Input
                                type="textarea"
                                placeholder="Description du produit"
                                className="textarea textarea-bordered w-full"
                                value={productDescription}
                                onChange={(e) => setProductDescription(e.target.value)}
                            />
                        </InputWrapper>
                    </div>

                    <div className="mt-6">
                        <Card
                            title='Configuration des variations'
                            customClassName='bg-transparent'
                            collapsible
                        >
                            <ProductVariation
                                ref={variationRef}
                                onChange={(variationType: VariationTypeEnum, data: any)=> {
                                    setVariationType(variationType);
                                    setVariations(data);
                                }}
                            />
                        </Card>
                    </div>
                </Card>
                <span className='md:hidden'>
                    {rendrButton()}
                </span>
            </div>
        </>

    );
};
